import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, UserCheck, UserX, Trash2, LogOut, ShieldCheck, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Admin() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerName, setRegisterName] = useState('');
  const [registerLoading, setRegisterLoading] = useState(false);

  useEffect(() => {
    const user = localStorage.getItem('user');
    if (user) {
      const userData = JSON.parse(user);
      setCurrentUser(userData);
      
      if (userData.role !== 'admin') {
        toast.error('Acesso negado: apenas administradores');
        navigate('/host');
        return;
      }
    } else {
      navigate('/login');
      return;
    }

    fetchUsers();
  }, [navigate]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Erro ao carregar utilizadores');
      if (error.response?.status === 401) {
        navigate('/login');
      }
      setLoading(false);
    }
  };

  const toggleUserStatus = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/users/${userId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Estado do utilizador atualizado');
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao atualizar utilizador');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Tem a certeza que deseja eliminar este utilizador?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Utilizador eliminado');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao eliminar utilizador');
    }
  };

  const approveUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/users/${userId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.status === 'approved' ? 'Utilizador aprovado' : 'Aprovacao removida');
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao aprovar utilizador');
    }
  };

  const registerUser = async (e) => {
    e.preventDefault();
    if (!registerEmail) { toast.error('Email obrigatorio'); return; }
    if (!registerPassword || registerPassword.length < 4) { toast.error('Password minimo 4 caracteres'); return; }
    setRegisterLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/users/register`, {
        email: registerEmail,
        password: registerPassword,
        name: registerName
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Utilizador registado com sucesso!');
      setRegisterEmail('');
      setRegisterPassword('');
      setRegisterName('');
      setShowRegisterForm(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registar utilizador');
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast.info('Sessao terminada');
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <img src="/logo-dih.png" alt="Do IT Happen" className="h-10" />
            <div>
              <h1 className="font-['Manrope'] text-3xl md:text-4xl font-bold" data-testid="admin-title">
                Painel Admin
              </h1>
              <p className="font-['Inter'] text-sm text-slate-400 mt-1">
                Bem-vindo, {currentUser?.name}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate('/host')}
              className="text-slate-400 hover:text-white"
              data-testid="go-to-host-btn"
            >
              Host Console
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/backoffice')}
              className="text-slate-400 hover:text-white"
              data-testid="go-to-backoffice-btn"
            >
              Backoffice
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-red-400 hover:text-red-300"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="font-['Manrope'] text-lg text-slate-300">Total Utilizadores</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-['Manrope'] text-4xl font-bold text-blue-400" data-testid="total-users">
                {users.length}
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="font-['Manrope'] text-lg text-slate-300">Ativos</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-['Manrope'] text-4xl font-bold text-green-400" data-testid="active-users">
                {users.filter(u => u.is_active).length}
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="font-['Manrope'] text-lg text-slate-300">Admins</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-['Manrope'] text-4xl font-bold text-purple-400" data-testid="admin-users">
                {users.filter(u => u.role === 'admin').length}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="font-['Manrope'] text-lg text-slate-300">Pendentes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-['Manrope'] text-4xl font-bold text-yellow-400" data-testid="pending-users">
                {users.filter(u => !u.is_approved && u.role !== 'admin').length}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Register User Section */}
        <div className="mb-6">
          {!showRegisterForm ? (
            <Button
              onClick={() => setShowRegisterForm(true)}
              className="bg-blue-600 hover:bg-blue-500 text-white"
              data-testid="show-register-form-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Registar Novo Utilizador
            </Button>
          ) : (
            <Card className="bg-slate-900 border-slate-800" data-testid="register-form-card">
              <CardHeader>
                <CardTitle className="font-['Manrope'] text-lg text-white">Registar Novo Utilizador</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={registerUser} className="flex flex-wrap items-end gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <label className="font-['Inter'] text-xs text-slate-400 block mb-1">Email *</label>
                    <input
                      type="email"
                      value={registerEmail}
                      onChange={(e) => setRegisterEmail(e.target.value)}
                      required
                      placeholder="email@exemplo.com"
                      className="w-full h-9 px-3 rounded-md bg-slate-800 border border-slate-700 text-white text-sm focus:border-blue-500 focus:outline-none"
                      data-testid="register-email-input"
                    />
                  </div>
                  <div className="w-48">
                    <label className="font-['Inter'] text-xs text-slate-400 block mb-1">Password *</label>
                    <input
                      type="password"
                      value={registerPassword}
                      onChange={(e) => setRegisterPassword(e.target.value)}
                      required
                      minLength={4}
                      placeholder="Min. 4 caracteres"
                      className="w-full h-9 px-3 rounded-md bg-slate-800 border border-slate-700 text-white text-sm focus:border-blue-500 focus:outline-none"
                      data-testid="register-password-input"
                    />
                  </div>
                  <div className="w-48">
                    <label className="font-['Inter'] text-xs text-slate-400 block mb-1">Nome (opcional)</label>
                    <input
                      type="text"
                      value={registerName}
                      onChange={(e) => setRegisterName(e.target.value)}
                      placeholder="Nome do utilizador"
                      className="w-full h-9 px-3 rounded-md bg-slate-800 border border-slate-700 text-white text-sm focus:border-blue-500 focus:outline-none"
                      data-testid="register-name-input"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      disabled={registerLoading}
                      className="bg-blue-600 hover:bg-blue-500 text-white h-9"
                      data-testid="register-submit-btn"
                    >
                      {registerLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Registar'}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setShowRegisterForm(false)}
                      className="text-slate-400 h-9"
                      data-testid="register-cancel-btn"
                    >
                      Cancelar
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Users Table */}
        <Card className="bg-slate-900 border-slate-800" data-testid="users-table-card">
          <CardHeader>
            <CardTitle className="font-['Manrope'] text-xl text-white">Gestao de Utilizadores</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800 border-b border-slate-700">
                  <tr>
                    <th className="font-['Inter'] text-left p-3 text-sm font-semibold text-slate-300">Nome</th>
                    <th className="font-['Inter'] text-left p-3 text-sm font-semibold text-slate-300">Email</th>
                    <th className="font-['Inter'] text-left p-3 text-sm font-semibold text-slate-300">Role</th>
                    <th className="font-['Inter'] text-left p-3 text-sm font-semibold text-slate-300">Estado</th>
                    <th className="font-['Inter'] text-left p-3 text-sm font-semibold text-slate-300">Aprovado</th>
                    <th className="font-['Inter'] text-left p-3 text-sm font-semibold text-slate-300">Criado</th>
                    <th className="font-['Inter'] text-left p-3 text-sm font-semibold text-slate-300">Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b border-slate-800 hover:bg-slate-800/50" data-testid="user-row">
                      <td className="font-['Inter'] p-3 text-sm text-white" data-testid="user-name">
                        {user.name}
                      </td>
                      <td className="font-['Inter'] p-3 text-sm text-slate-400" data-testid="user-email">
                        {user.email}
                      </td>
                      <td className="p-3">
                        <span className={`font-['Inter'] text-xs px-2 py-1 rounded ${
                          user.role === 'admin' 
                            ? 'bg-purple-900/50 text-purple-400 border border-purple-700' 
                            : 'bg-blue-900/50 text-blue-400 border border-blue-700'
                        }`} data-testid="user-role">
                          {user.role}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className={`font-['Inter'] text-xs px-2 py-1 rounded ${
                          user.is_active 
                            ? 'bg-green-900/50 text-green-400 border border-green-700' 
                            : 'bg-red-900/50 text-red-400 border border-red-700'
                        }`} data-testid="user-status">
                          {user.is_active ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className={`font-['Inter'] text-xs px-2 py-1 rounded ${
                          user.is_approved || user.role === 'admin'
                            ? 'bg-green-900/50 text-green-400 border border-green-700' 
                            : 'bg-yellow-900/50 text-yellow-400 border border-yellow-700'
                        }`} data-testid="user-approved">
                          {user.is_approved || user.role === 'admin' ? 'Sim' : 'Pendente'}
                        </span>
                      </td>
                      <td className="font-['Inter'] p-3 text-sm text-slate-400" data-testid="user-created">
                        {new Date(user.created_at).toLocaleDateString('pt-PT')}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          {user.role !== 'admin' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => approveUser(user.id)}
                              className={user.is_approved ? "text-green-400 hover:text-green-300" : "text-yellow-400 hover:text-yellow-300"}
                              title={user.is_approved ? 'Remover aprovacao' : 'Aprovar utilizador'}
                              data-testid={`approve-user-${user.id}`}
                            >
                              <ShieldCheck className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleUserStatus(user.id)}
                            className="text-slate-400 hover:text-white"
                            data-testid={`toggle-user-${user.id}`}
                          >
                            {user.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                          </Button>
                          {user.role !== 'admin' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteUser(user.id)}
                              className="text-red-400 hover:text-red-300"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
